// Add / Edit Carrier modal — FE-SCRM-030 Steps 5 + 6.
//
// One component, two modes: ``create`` and ``edit``. Required fields
// (name, carrier_type) are validated client-side; backend errors come
// back as the inline ``form_error`` string. Submitting calls the
// appropriate mutation through the caller's ``onSubmit`` handler so
// the modal stays presentation-only.

import { useEffect, useMemo, useState } from "react";

import { Modal } from "@/design-system";

import { CARRIER_TYPE_LABEL } from "./labels";
import {
  CARRIER_STATUSES,
  CARRIER_TYPES,
  type CarrierDetail,
  type CarrierStatus,
  type CarrierType,
  type CarrierWriteInput,
} from "./types";


export type CarrierFormMode = "create" | "edit";


interface Props {
  open: boolean;
  mode: CarrierFormMode;
  initial?: CarrierDetail | null;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (values: CarrierWriteInput) => void;
}


export function CarrierFormModal({
  open, mode, initial, isSubmitting, errorMessage, onClose, onSubmit,
}: Props) {
  const [name, setName] = useState("");
  const [carrierType, setCarrierType] = useState<CarrierType>("multi_line");
  const [naicCode, setNaicCode] = useState("");
  const [website, setWebsite] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [status, setStatus] = useState<CarrierStatus>("active");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);

  // Reset form whenever the modal opens or the ``initial`` row swaps.
  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setCarrierType(initial?.carrier_type ?? "multi_line");
    setNaicCode(initial?.naic_code ?? "");
    setWebsite(initial?.website ?? "");
    setLogoUrl(initial?.logo_url ?? "");
    setStatus(initial?.status ?? "active");
    setNotes(initial?.notes ?? "");
    setTouched(false);
  }, [open, initial]);

  const nameError = useMemo(() => {
    if (!touched) return null;
    if (!name.trim()) return "Name is required";
    return null;
  }, [name, touched]);

  const naicError = useMemo(() => {
    if (!touched || !naicCode.trim()) return null;
    if (!/^\d{5}$/.test(naicCode.trim())) {
      return "NAIC code must be a 5-digit number";
    }
    return null;
  }, [naicCode, touched]);

  const websiteError = useMemo(() => {
    if (!touched || !website.trim()) return null;
    try {
      const u = new URL(website.trim());
      if (!u.protocol.startsWith("http")) {
        return "Website must start with http(s)://";
      }
      return null;
    } catch {
      return "Website must be a valid URL";
    }
  }, [website, touched]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!name.trim() || naicError || websiteError) return;
    onSubmit({
      name: name.trim(),
      carrier_type: carrierType,
      naic_code: naicCode.trim() || null,
      website: website.trim() || null,
      logo_url: logoUrl.trim() || null,
      status,
      notes: notes.trim() || null,
    });
  }

  const title = mode === "create" ? "Add carrier" : "Edit carrier";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="carrier-form-cancel"
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="carrier-form"
            disabled={isSubmitting}
            data-testid="carrier-form-submit"
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-60"
          >
            {isSubmitting
              ? (mode === "create" ? "Adding…" : "Saving…")
              : (mode === "create" ? "Add carrier" : "Save changes")}
          </button>
        </>
      }
    >
      <form
        id="carrier-form"
        data-testid="carrier-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        {errorMessage && (
          <div
            data-testid="carrier-form-error"
            role="alert"
            className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember-ink"
          >
            {errorMessage}
          </div>
        )}

        <Field label="Name" required>
          <input
            type="text"
            data-testid="carrier-form-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          />
          {nameError && (
            <p className="mt-1 text-xs text-ember-ink" role="alert">
              {nameError}
            </p>
          )}
        </Field>

        <Field label="Carrier type" required>
          <select
            data-testid="carrier-form-type"
            value={carrierType}
            onChange={(e) => setCarrierType(e.target.value as CarrierType)}
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          >
            {CARRIER_TYPES.map((t) => (
              <option key={t} value={t}>
                {CARRIER_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="NAIC code">
          <input
            type="text"
            data-testid="carrier-form-naic"
            value={naicCode}
            onChange={(e) => setNaicCode(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="e.g. 70670"
            inputMode="numeric"
            maxLength={5}
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          />
          {naicError && (
            <p className="mt-1 text-xs text-ember-ink" role="alert">
              {naicError}
            </p>
          )}
        </Field>

        <Field label="Website">
          <input
            type="url"
            data-testid="carrier-form-website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="https://carrier.example.com"
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          />
          {websiteError && (
            <p className="mt-1 text-xs text-ember-ink" role="alert">
              {websiteError}
            </p>
          )}
        </Field>

        <Field label="Status">
          <select
            data-testid="carrier-form-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as CarrierStatus)}
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          >
            {CARRIER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Notes">
          <textarea
            data-testid="carrier-form-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-sm text-text-primary outline-none focus:border-teal focus:shadow-focus"
          />
        </Field>
      </form>
    </Modal>
  );
}


function Field({
  label, required, children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs font-medium text-text-secondary">
        {label}
        {required && <span className="text-ember-ink"> *</span>}
      </span>
      {children}
    </label>
  );
}
